import { roundLabel } from './printLabels';
import type { CompetitionBracketData, CompetitionMatch } from '../../../types';

/** One left-to-right column of the printed bracket tree (a single round). */
export interface BracketColumn {
  round: number;
  label: string;
  matches: CompetitionMatch[];
}

/** A single printed A4 bracket sheet. */
export interface BracketPage {
  key: string;
  /** Big header title: "Chave", "Chave A", "Fase Final". */
  title: string;
  /** Descriptive line under the header. */
  subtitle: string;
  /** Columns rendered left→right. */
  columns: BracketColumn[];
  /** When set, the page renders the 3º-lugar + Campeão footer. */
  footer: { thirdPlace: CompetitionMatch | null } | null;
}

const QUADRANT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const byPosition = (a: CompetitionMatch, b: CompetitionMatch) =>
  (a.bracketPosition ?? 0) - (b.bracketPosition ?? 0);

/**
 * Split the knockout bracket into printable A4 pages, keeping the classic
 * left-to-right tree the user settled on.
 *
 * - ≤ 3 rounds (≤ 8 teams): one page with every round as a column + footer.
 * - ≥ 4 rounds (16-team Oitavas … 32-team R32): the conventional split —
 *   one "quadrant" page per match in the round that feeds the semifinals
 *   (the quarter-finals, ~4 matches), each showing that match's full subtree;
 *   then a "Fase Final" page with the semifinals, final, 3º lugar and Campeão.
 *   Since the deepest supported draw is 5 rounds, a quadrant page never has
 *   more than 3 columns, so it fits A4 portrait width.
 */
export function buildBracketPages(bracket: CompetitionBracketData | null): BracketPage[] {
  if (!bracket || bracket.rounds.length === 0) return [];

  const rounds = [...bracket.rounds].sort((a, b) => a.round - b.round);
  const total = rounds.length;

  // The 3rd-place playoff lives in the last round but renders separately.
  const thirdPlace = rounds[total - 1].matches.find((m) => m.isThirdPlace) ?? null;

  // Main (non-3rd-place) matches per round, position-sorted.
  const mainByRound = rounds.map((r) => ({
    round: r.round,
    matches: r.matches.filter((m) => !m.isThirdPlace).sort(byPosition),
  }));

  const toColumn = (r: { round: number; matches: CompetitionMatch[] }): BracketColumn => ({
    round: r.round,
    label: roundLabel(r.round, total),
    matches: r.matches,
  });

  // Small draw → a single tree page.
  if (total <= 3) {
    return [
      {
        key: 'chave',
        title: 'Chave',
        subtitle: 'Mata-mata · eliminação simples',
        columns: mainByRound.map(toColumn),
        footer: { thirdPlace },
      },
    ];
  }

  // Large draw → quadrant pages + a final page.
  const finalRounds = mainByRound.slice(total - 2); // semifinals + final
  const preRounds = mainByRound.slice(0, total - 2); // everything before the semis
  const anchorRound = preRounds[preRounds.length - 1]; // the round feeding the semis (quarters)

  // Reverse-link each match to the matches whose winner feeds it.
  const feeders = new Map<string, CompetitionMatch[]>();
  for (const r of mainByRound) {
    for (const m of r.matches) {
      if (!m.nextMatchId) continue;
      const list = feeders.get(m.nextMatchId);
      if (list) list.push(m);
      else feeders.set(m.nextMatchId, [m]);
    }
  }

  const pages: BracketPage[] = anchorRound.matches.map((anchor, i) => {
    // Collect the anchor's whole subtree (anchor + feeders, recursively).
    const subtreeByRound = new Map<number, CompetitionMatch[]>();
    const visit = (m: CompetitionMatch) => {
      const round = m.bracketRound ?? 0;
      const list = subtreeByRound.get(round);
      if (list) list.push(m);
      else subtreeByRound.set(round, [m]);
      for (const f of feeders.get(m.id) ?? []) visit(f);
    };
    visit(anchor);

    const columns: BracketColumn[] = preRounds
      .filter((r) => subtreeByRound.has(r.round))
      .map((r) => ({
        round: r.round,
        label: roundLabel(r.round, total),
        matches: (subtreeByRound.get(r.round) ?? []).sort(byPosition),
      }));

    return {
      key: `chave-${i}`,
      title: `Chave ${QUADRANT_LETTERS[i] ?? i + 1}`,
      subtitle: 'Caminho até a semifinal · o vencedor da última coluna avança',
      columns,
      footer: null,
    };
  });

  pages.push({
    key: 'fase-final',
    title: 'Fase Final',
    subtitle: 'Semifinais e final · decididas a partir das chaves',
    columns: finalRounds.map(toColumn),
    footer: { thirdPlace },
  });

  return pages;
}

/** Number of distinct group-qualification slots = duplas entering the bracket. */
export function countBracketTeams(bracket: CompetitionBracketData): number {
  const slots = new Set<string>();
  for (const round of bracket.rounds) {
    for (const match of round.matches) {
      for (const source of [match.team1Source, match.team2Source]) {
        if (source?.kind === 'group') slots.add(`${source.groupNumber}-${source.rank}`);
      }
    }
  }
  return slots.size;
}
